
import React from 'react';
import { Link } from 'react-router-dom';
import { SearchResult } from '@/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { formatDuration } from '@/lib/utils';
import { Play, Scissors, ExternalLink, Clock, User, Tag, MapPin } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface SemanticResultCardProps {
  result: SearchResult;
  onGenerateClip: () => void;
}

export default function SemanticResultCard({ result, onGenerateClip }: SemanticResultCardProps) {
  const getVideoThumbnail = (videoId: string) => {
    // Generate thumbnail based on video ID - in a real app this would come from the video data
    const imageId = 1500000000000 + parseInt(videoId.replace('v', ''));
    return `https://images.unsplash.com/photo-${imageId}?w=320&h=180&fit=crop`;
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'bg-green-100 text-green-800';
    if (confidence >= 0.6) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const highlightSearchTerms = (text: string) => {
    // In a real implementation, this would highlight the actual search terms
    // For now, we'll return the text as-is
    return text;
  };

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-all duration-300">
      <CardContent className="p-0">
        <div className="flex flex-col md:flex-row">
          {/* Video thumbnail with timestamp overlay */}
          <div className="relative md:w-80 h-48">
            <Link to={`/videos/${result.videoId}?t=${result.startTime}`}>
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-60 z-10" />
              <img 
                src={getVideoThumbnail(result.videoId)}
                alt="Video frame" 
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
              />
              
              {/* Timestamp badge */}
              {result.startTime !== undefined && (
                <div className="absolute bottom-3 right-3 bg-video-blue text-white text-sm px-3 py-1 rounded-full font-medium z-20">
                  <Clock className="w-4 h-4 inline mr-1" />
                  {formatDuration(result.startTime)}
                </div>
              )}
              
              {/* Play overlay */}
              <div className="absolute inset-0 flex items-center justify-center z-20">
                <div className="rounded-full bg-white/20 p-4 hover:bg-white/30 transition-colors">
                  <Play className="h-8 w-8 text-white" />
                </div>
              </div>
              
              {/* Content type indicator */}
              <div className="absolute top-3 left-3 z-20">
                <Badge 
                  variant={result.type === 'segment' ? 'default' : 'secondary'}
                  className="text-xs"
                >
                  {result.type === 'segment' ? 'Speech' : 'Visual'}
                </Badge>
              </div>
            </Link>
          </div>
          
          {/* Result details */}
          <div className="p-6 flex-1 flex flex-col">
            {/* Header with metadata */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                {result.speaker && (
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <User className="w-4 h-4" />
                    <span>{result.speaker}</span>
                  </div>
                )}
                
                {result.startTime !== undefined && result.endTime !== undefined && (
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <MapPin className="w-4 h-4" />
                    <span>
                      {formatDuration(result.startTime)} - {formatDuration(result.endTime)}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Confidence score */}
              <Badge 
                className={`text-xs ${getConfidenceColor(result.relevance)}`}
                variant="secondary"
              >
                {Math.round(result.relevance * 100)}% match
              </Badge>
            </div>
            
            {/* Main content */}
            <div className="flex-1 mb-4">
              {result.text && (
                <div className="mb-4">
                  <p className="text-base leading-relaxed">
                    "{highlightSearchTerms(result.text)}"
                  </p>
                </div>
              )}
              
              {/* Visual objects detected */}
              {result.objects && result.objects.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Tag className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium text-muted-foreground">Objects detected:</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {result.objects.map((object, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {object}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Scene context */}
              {result.sceneContext && (
                <div className="text-sm text-muted-foreground mb-4">
                  <strong>Scene:</strong> {result.sceneContext}
                </div>
              )}
            </div>
            
            {/* Action buttons */}
            <div className="flex flex-wrap gap-3 mt-auto">
              <Button size="sm" asChild>
                <Link to={`/videos/${result.videoId}?t=${result.startTime}`}>
                  <Play className="w-4 h-4 mr-2" />
                  Watch from here
                </Link>
              </Button>
              
              <Button size="sm" variant="outline" onClick={onGenerateClip}>
                <Scissors className="w-4 h-4 mr-2" />
                Create clip
              </Button>
              
              <Button size="sm" variant="ghost" asChild>
                <Link to={`/videos/${result.videoId}`}>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Full video
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
